import { useCallback } from 'react';
import { useMutation, ApolloError } from '@apollo/client';
import { ModifiersPayload } from 'controllers/SearchController/types';
import {
    ContentSuggestionPayload,
    isContentSuggestionValue,
} from 'components/Filters/context/SearchBarContext/types';
import {
    DeleteUserRecentSearchMutation,
    DeleteUserRecentSearchMutationVariables,
} from 'types/generated/graphql';
import { useNotificationsContext } from 'contexts/NotificationsContext/NotificationsContext';
import { DeleteUserRecentSearchError } from 'contexts/NotificationsContext/notifications';
import getFilterParamsToType from './helpers/getFilterParamsToType';
import {
    DeleteUserRecentSearch,
    GetUserRecentSearch,
} from './userRecentSearch.gql';

const useDeleteUserRecentSearch = ({
    isAuthenticated,
}: {
    isAuthenticated: boolean;
}) => {
    const { createNotification } = useNotificationsContext();
    const [deleteUserRecentSearch] = useMutation<
        DeleteUserRecentSearchMutation,
        DeleteUserRecentSearchMutationVariables
    >(DeleteUserRecentSearch, {
        refetchQueries: [
            {
                query: GetUserRecentSearch,
            },
        ],
        onError: (error: ApolloError) => {
            createNotification(new DeleteUserRecentSearchError());
        },
    });

    const handleDeleteUserRecentSearch = useCallback(
        (modifiers: (ModifiersPayload | ContentSuggestionPayload)[]) => {
            if (!isAuthenticated) {
                return;
            }

            modifiers.forEach(([params, value]) => {
                const filterType = getFilterParamsToType(params);
                const filterSlug = isContentSuggestionValue(value)
                    ? value.slug
                    : value;

                if (filterType && filterSlug) {
                    deleteUserRecentSearch({
                        variables: {
                            filterType,
                            filterSlug,
                        },
                    });
                }
            });
        },
        [deleteUserRecentSearch, isAuthenticated],
    );

    return handleDeleteUserRecentSearch;
};

export default useDeleteUserRecentSearch;
